import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, Button, Table, Modal, Form, Input, message, Tabs, Space, Tooltip, Upload, Alert } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { UPLOAD_RULES, uploadHint, uploadProps } from "utils/uploadRules";
import {
  getStatesByRole,
  getCleaningAuditRoleId,
  getFacilitiesByState,
  getAreaRiskTypes,
  getFloorsByFacility,
  addFloor,
  updateFloor,
  deleteFloor,
  getAreasByFacility,
  addArea,
  updateArea,
  deleteArea,
  getRoomsByFacility,
  addRoom,
  updateRoom,
  deleteRoom,
  bulkUploadFacilitySetup,
} from "./cleaningAuditApi";

const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;

const csvUpload = uploadProps(UPLOAD_RULES.CSV);

// One CSV row per room. The first row must be this header (the server checks
// it); the server reads the columns in this order, finds the state and the
// facility (within the client) by name, and adds any floor or area that does
// not exist yet. The area type must already be set up for the state.
const SAMPLE_HEADER = ["State", "Facility", "Floor", "Area Name", "Area Type", "Room Name"];

const csvLine = (values) => values.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");

const downloadSample = ({ stateName, facilityName, riskTypes }) => {
  const state = stateName || "Victoria";
  const facility = facilityName || "Example Facility";
  const type = riskTypes[0]?.name || "High Risk";
  const rows = [SAMPLE_HEADER, [state, facility, "Ground Floor", "Bathroom 1", type, "Shower"], [state, facility, "Ground Floor", "Bathroom 1", type, "Toilet"]];
  const blob = new Blob([rows.map(csvLine).join("\r\n") + "\r\n"], { type: "text/csv" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "facility-setup-sample.csv";
  link.click();
  URL.revokeObjectURL(link.href);
};

// the file as base64, without the "data:...;base64," prefix the API does not expect
const readBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Add Bulk Data, as on the old Cleaning Audit web: floors, areas and rooms for
// any facilities from one CSV.
const BulkUploadModal = ({ visible, stateName, facilityName, riskTypes, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [outcome, setOutcome] = useState(null); // { added, existing, errors }

  useEffect(() => {
    if (visible) { setFile(null); setOutcome(null); }
  }, [visible]);

  const upload = async () => {
    if (!file) return message.error("Please select the CSV file.");
    setUploading(true);
    const { error, result } = await bulkUploadFacilitySetup(await readBase64(file));
    setUploading(false);
    if (error || !result?.success) return message.error(result?.message || "The file could not be uploaded. Please try again.");
    // each error names its CSV row; summary counts what was really added
    const errors = result.result?.errors || [];
    const summary = result.result?.summary;
    const added = summary ? summary.rooms_added : (result.result?.processedData || []).length;
    setOutcome({ added, existing: summary ? summary.rooms_existing : 0, errors });
    setFile(null);
    if (added > 0) onDone();
  };

  return (
    <Modal
      title="Add Bulk Data"
      visible={visible}
      onCancel={() => { if (!uploading) onClose(); }}
      maskClosable={!uploading}
      destroyOnClose
      footer={[
        <Button key="close" disabled={uploading} onClick={onClose}>Close</Button>,
        <Button key="upload" type="primary" icon={<UploadOutlined />} loading={uploading} disabled={!file} onClick={upload}>Upload</Button>,
      ]}
    >
      <p style={{ color: "#5B6472" }}>
        Add floors, areas and rooms from a CSV file, one row per room. Download the sample sheet, fill it in and upload it,
        keeping its header row. Floors and areas that do not exist yet are created; the area type must already be set up for the state.
      </p>
      <Button icon={<DownloadOutlined />} onClick={() => downloadSample({ stateName, facilityName, riskTypes })} style={{ marginBottom: 16 }}>
        Download Sample Sheet
      </Button>
      <Upload
        {...csvUpload}
        fileList={file ? [file] : []}
        beforeUpload={(f) => {
          const checked = csvUpload.beforeUpload(f); // a rejected promise when the file is refused
          if (checked === false) setFile(f);
          return checked;
        }}
        onRemove={() => setFile(null)}
      >
        <Button icon={<UploadOutlined />}>Select CSV File</Button>
      </Upload>
      <div style={{ fontSize: 12, color: "#5B6472", marginTop: 4 }}>{uploadHint(UPLOAD_RULES.CSV)}</div>
      {outcome && (
        <Alert
          style={{ marginTop: 16 }}
          type={outcome.errors.length ? "warning" : "success"}
          showIcon
          message={`${outcome.added} room${outcome.added === 1 ? "" : "s"} added${outcome.existing ? `, ${outcome.existing} already there` : ""}${outcome.errors.length ? `, ${outcome.errors.length} row${outcome.errors.length === 1 ? "" : "s"} not added` : ""}.`}
          description={outcome.errors.length ? (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: "auto" }}>
              {outcome.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          ) : null}
        />
      )}
    </Modal>
  );
};

const FloorFormModal = ({ visible, editing, facilityId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) form.setFieldsValue({ name: editing?.name || "" });
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { facility_id: facilityId, name: values.name };
      const { error, result } = editing ? await updateFloor({ ...payload, id: editing.id }) : await addFloor(payload);
      if (error) message.error(result?.message || "Failed to save floor.");
      else { message.success(editing ? "Floor updated." : "Floor added."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Floor" : "Add Floor"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Floor Name" rules={[{ required: true, message: "Please enter the floor name." }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const AreaFormModal = ({ visible, editing, floors, riskTypes, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        floor_id: editing?.floor_id,
        area_risk_type_id: editing?.area_risk_type_id,
        name: editing?.name || "",
      });
    }
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { error, result } = editing
        ? await updateArea({ ...values, id: editing.id })
        : await addArea(values);
      if (error) message.error(result?.message || "Failed to save area.");
      else { message.success(editing ? "Area updated." : "Area added."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Area" : "Add Area"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="floor_id" label="Floor" rules={[{ required: true, message: "Please select a floor." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select a floor">
            {floors.map((f) => <Option key={f.id} value={f.id}>{f.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="area_risk_type_id" label="Area Type" rules={[{ required: true, message: "Please select an area type." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select an area type">
            {riskTypes.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="name" label="Area Name" rules={[{ required: true, message: "Please enter the area name." }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const RoomFormModal = ({ visible, editing, areas, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) form.setFieldsValue({ area_id: editing?.area_id, name: editing?.name || "" });
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const { error, result } = editing
        ? await updateRoom({ ...values, id: editing.id })
        : await addRoom(values);
      if (error) message.error(result?.message || "Failed to save room.");
      else { message.success(editing ? "Room updated." : "Room added."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? "Edit Room" : "Add Room"} visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="area_id" label="Area" rules={[{ required: true, message: "Please select an area." }]}>
          <Select showSearch optionFilterProp="children" placeholder="Select an area">
            {areas.map((a) => <Option key={a.id} value={a.id}>{a.floor_name} — {a.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="name" label="Room Name" rules={[{ required: true, message: "Please enter the room name." }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const FacilitySetup = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [riskTypes, setRiskTypes] = useState([]);
  const [floors, setFloors] = useState([]);
  const [areas, setAreas] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [floorModal, setFloorModal] = useState({ visible: false, editing: null });
  const [areaModal, setAreaModal] = useState({ visible: false, editing: null });
  const [roomModal, setRoomModal] = useState({ visible: false, editing: null });
  const [bulkVisible, setBulkVisible] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const roleId = await getCleaningAuditRoleId();
        const { error, result } = await getStatesByRole(roleId);
        setStates(!error && Array.isArray(result?.result) ? result.result : []);
      } catch (e) {
        setStates([]);
        message.error({ key: "cleaning-audit-connect", content: "Couldn't connect to Cleaning Audit. Please try again shortly." });
      }
    })();
  }, []);

  const onStateChange = useCallback(async (value) => {
    setStateId(value);
    setFacilityId(null);
    setFacilities([]);
    setRiskTypes([]);
    if (!value) return;
    const [facRes, riskRes] = await Promise.all([getFacilitiesByState(value), getAreaRiskTypes(value)]);
    setFacilities(!facRes.error && Array.isArray(facRes.result?.result) ? facRes.result.result : []);
    setRiskTypes(!riskRes.error && Array.isArray(riskRes.result?.result) ? riskRes.result.result : []);
  }, []);

  const load = useCallback(async () => {
    if (!facilityId) {
      message.error("Please select a state and facility first.");
      return;
    }
    setLoading(true);
    setLoaded(true);
    const [floorsRes, areasRes, roomsRes] = await Promise.all([
      getFloorsByFacility(facilityId),
      getAreasByFacility(facilityId),
      getRoomsByFacility(facilityId),
    ]);
    setFloors(!floorsRes.error && Array.isArray(floorsRes.result?.result) ? floorsRes.result.result : []);
    setAreas(!areasRes.error && Array.isArray(areasRes.result?.result) ? areasRes.result.result : []);
    setRooms(!roomsRes.error && Array.isArray(roomsRes.result?.result) ? roomsRes.result.result : []);
    setLoading(false);
  }, [facilityId]);

  const confirmDelete = (title, content, action) => {
    confirm({
      title,
      content,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await action();
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(); }
      },
    });
  };

  const floorColumns = [
    { title: "Floor Name", dataIndex: "name", key: "name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setFloorModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete("Delete floor", `Delete "${row.name}"? Its areas and rooms will be affected.`, () => deleteFloor(row.id))} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  const areaColumns = [
    { title: "Floor Name", dataIndex: "floor_name", key: "floor_name" },
    { title: "Area Type", dataIndex: "area_cat_name", key: "area_cat_name" },
    { title: "Area Name", dataIndex: "name", key: "name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setAreaModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete("Delete area", `Delete "${row.name}"? Its rooms will be affected.`, () => deleteArea(row.id))} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  const roomColumns = [
    { title: "Floor Name", dataIndex: "floor_name", key: "floor_name" },
    { title: "Area Name", dataIndex: "area_name", key: "area_name" },
    { title: "Room Name", dataIndex: "name", key: "name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setRoomModal({ visible: true, editing: row })} type="primary" aria-label="Edit" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete("Delete room", `Delete "${row.name}"?`, () => deleteRoom(row.id))} aria-label="Delete" /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select showSearch optionFilterProp="children" placeholder="Select a state" style={{ width: 200 }} value={stateId} onChange={onStateChange}>
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a facility"
              style={{ width: 220 }}
              value={facilityId}
              onChange={setFacilityId}
              disabled={!stateId}
              notFoundContent={stateId ? "No facilities for this state" : "Select a state first"}
            >
              {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={load}>Load</Button>
          <Button icon={<PlusOutlined />} style={{ marginLeft: "auto" }} onClick={() => setBulkVisible(true)}>Add Bulk Data</Button>
        </div>
      </Card>

      {loaded && (
        <Card>
          <Tabs defaultActiveKey="floors">
            <TabPane tab={`Floors (${floors.length})`} key="floors">
              <div style={{ textAlign: "right", marginBottom: 12 }}>
                <Button icon={<PlusOutlined />} onClick={() => setFloorModal({ visible: true, editing: null })}>Add Floor</Button>
              </div>
              <Table rowKey="id" columns={floorColumns} dataSource={floors} loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText: "No floors yet." }} />
            </TabPane>
            <TabPane tab={`Areas (${areas.length})`} key="areas">
              <div style={{ textAlign: "right", marginBottom: 12 }}>
                <Button icon={<PlusOutlined />} onClick={() => setAreaModal({ visible: true, editing: null })} disabled={!floors.length}>Add Area</Button>
              </div>
              <Table rowKey="id" columns={areaColumns} dataSource={areas} loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText: "No areas yet." }} />
            </TabPane>
            <TabPane tab={`Rooms (${rooms.length})`} key="rooms">
              <div style={{ textAlign: "right", marginBottom: 12 }}>
                <Button icon={<PlusOutlined />} onClick={() => setRoomModal({ visible: true, editing: null })} disabled={!areas.length}>Add Room</Button>
              </div>
              <Table rowKey="id" columns={roomColumns} dataSource={rooms} loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText: "No rooms yet." }} />
            </TabPane>
          </Tabs>
        </Card>
      )}

      <FloorFormModal
        visible={floorModal.visible}
        editing={floorModal.editing}
        facilityId={facilityId}
        onClose={() => setFloorModal({ visible: false, editing: null })}
        onSaved={load}
      />
      <AreaFormModal
        visible={areaModal.visible}
        editing={areaModal.editing}
        floors={floors}
        riskTypes={riskTypes}
        onClose={() => setAreaModal({ visible: false, editing: null })}
        onSaved={load}
      />
      <RoomFormModal
        visible={roomModal.visible}
        editing={roomModal.editing}
        areas={areas}
        onClose={() => setRoomModal({ visible: false, editing: null })}
        onSaved={load}
      />
      <BulkUploadModal
        visible={bulkVisible}
        stateName={(states.find((s) => s.state_id === stateId) || {}).state_name}
        facilityName={(facilities.find((f) => f.facility_id === facilityId) || {}).facility_name}
        riskTypes={riskTypes}
        onClose={() => setBulkVisible(false)}
        onDone={() => { if (loaded && facilityId) load(); }}
      />
    </div>
  );
};

export default memo(FacilitySetup);
